// Private to @github-ui/sanitize. NOT exported from package.json — the
// `github-sanitize` Trusted Types policy must only be reachable through
// sanitize() / sanitizeWithPurifier(), so that anything assigned to a
// TrustedHTML sink in CSP-`trusted-types`-enforced contexts has actually
// passed through DOMPurify.
//
// The generic policy mechanism (lazy creation, memoization, duplicate
// tolerance, metrics, and test seams) lives in @github-ui/trusted-types; this
// module only supplies the `github-sanitize` name, flag, and rules and keeps
// the resulting binding private.
import {createTrustedTypesPolicy} from '@github-ui/trusted-types'

const sanitizePolicy = createTrustedTypesPolicy({
  name: 'github-sanitize',
  createHTML: input => input,
  // DOMPurify requires `createScriptURL` on any policy it consumes via its
  // `TRUSTED_TYPES_POLICY` config; identity is safe because DOMPurify only
  // forwards already-validated URLs to this hook.
  createScriptURL: input => input,
})

/**
 * Lazily create and memoize the `github-sanitize` Trusted Types policy.
 * Returns null when Trusted Types are unavailable, the feature flag is
 * off, or the policy could not be created.
 */
export const getSanitizePolicy = sanitizePolicy.getPolicy

/**
 * Wrap an already-sanitized HTML string as a `TrustedHTML` when the
 * `github-sanitize` policy is active. Falls back to returning the input
 * branded as `SafeHTMLString` when Trusted Types are unavailable or the
 * policy could not be created.
 *
 * The input MUST already be DOMPurify output — this is what justifies
 * branding the fallback. Keep this helper package-private.
 */
export const mintTrustedHTML = sanitizePolicy.mint

/**
 * Reset the memoized policy. Test-only — not part of the public package API.
 */
export const _resetSanitizePolicyForTests = sanitizePolicy._resetForTests

/**
 * Inject a fake `TrustedTypePolicyFactory` (or `null` to simulate the
 * platform global being absent). Test-only — pass `undefined` to revert
 * to the real platform global.
 */
export const _setTrustedTypesForTests = sanitizePolicy._setTrustedTypesForTests
